var _excluded = ["userColumnWidthsByName"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import deepEqual from 'fast-deep-equal';
import memoizeOne from 'memoize-one';
import { GridMetricCalculator, trimMap, isExpandableColumnGridModel } from '@deephaven/grid';
import { assertNotNull } from '@deephaven/utils';
import { rebalanceTree } from "./TreeRebalanceUtil.js";
export class IrisGridMetricCalculator extends GridMetricCalculator {
  constructor() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var userColumnWidthsByName = options.userColumnWidthsByName,
      rest = _objectWithoutProperties(options, _excluded);
    super(rest);
    // Column widths by name to keep track of columns going in and out of viewport.
    _defineProperty(this, "userColumnWidthsByName", void 0);
    // Cached model column names to detect when the column width map update is necessary
    _defineProperty(this, "cachedModelColumnNames", void 0);
    _defineProperty(this, "cachedHeaderGroupNames", void 0);
    // Cached padding maps for column header groups
    _defineProperty(this, "cachedPaddingMaps", new Map());
    _defineProperty(this, "getCachedCurrentModelColumnNames", memoizeOne(columns => columns.map(col => col.name)));
    _defineProperty(this, "getCachedCurrentHeaderGroupNames", memoizeOne(columnHeaderGroups => columnHeaderGroups.map(group => group.name)));
    this.userColumnWidthsByName = userColumnWidthsByName !== null && userColumnWidthsByName !== void 0 ? userColumnWidthsByName : new Map();
  }
  static getModelColumnRoot(model, modelColumn) {
    var depth = 0;
    var current = model.getColumnHeaderParentGroup(modelColumn, depth);
    var root = current;
    while (current != null) {
      root = current;
      depth += 1;
      current = model.getColumnHeaderParentGroup(modelColumn, depth);
    }
    return root;
  }

  /**
   * Builds a TreeNode from the model header groups and columns for padding calculation
   * @param name The name of the root node to build the tree for
   * @param model The IrisGridModel containing columns and header groups
   * @param getLeafValue Function to get the value for leaf nodes (columns)
   * @param getGroupValue Function to get the value for group nodes (header groups)
   * @returns The TreeNode structure
   */
  static buildNode(name, model, getLeafValue, getGroupValue) {
    var _getLeafValue;
    var headerGroup = model.columnHeaderGroupMap.get(name);
    if (headerGroup) {
      var value = getGroupValue(name);
      var children = headerGroup.children.map(childName => IrisGridMetricCalculator.buildNode(childName, model, getLeafValue, getGroupValue));

      // Group node
      return {
        name,
        children,
        value
      };
    }

    // Leaf node
    return {
      name,
      children: [],
      value: (_getLeafValue = getLeafValue(name)) !== null && _getLeafValue !== void 0 ? _getLeafValue : 0
    };
  }

  /**
   * Gets the header padding for a specific column based on the widths of its group tree nodes.
   * We only adjust paddings on the column, because the group widths are automatically sized to fit their children.
   * @param state The current IrisGridMetricState
   * @param modelColumn The column index to get the padding for
   * @param maxColumnWidth Maximum allowed column width, applies only to leaf nodes
   * @returns The calculated header padding for the column
   */
  getHeaderPadding(state, modelColumn, maxColumnWidth) {
    var _paddingMap$get;
    var model = state.model;
    var root = IrisGridMetricCalculator.getModelColumnRoot(model, modelColumn);
    if (root == null) {
      return 0;
    }
    var cachedMap = this.cachedPaddingMaps.get(root.name);
    if (cachedMap != null) {
      var _cachedMap$get;
      return (_cachedMap$get = cachedMap.get(model.columns[modelColumn].name)) !== null && _cachedMap$get !== void 0 ? _cachedMap$get : 0;
    }
    var groupTree = IrisGridMetricCalculator.buildNode(root.name, model, name => {
      var columnIndex = model.getColumnIndexByName(name);
      assertNotNull(columnIndex, "".concat(name, " not found in model columns"));
      return super.calculateColumnHeaderWidth(columnIndex, state, maxColumnWidth);
    }, name => {
      var group = model.columnHeaderGroupMap.get(name);
      assertNotNull(group, "".concat(name, " not found in columnHeaderGroupMap"));
      return this.getColumnHeaderGroupWidth(group.childIndexes[0], group.depth, state, maxColumnWidth);
    });
    var paddingMap = rebalanceTree(groupTree);
    this.cachedPaddingMaps.set(root.name, paddingMap);
    return (_paddingMap$get = paddingMap.get(model.columns[modelColumn].name)) !== null && _paddingMap$get !== void 0 ? _paddingMap$get : 0;
  }
  updateCalculatedColumnWidths(model) {
    assertNotNull(this.cachedModelColumnNames);
    var calculatedColumnWidthsByName = new Map();
    this.cachedModelColumnNames.forEach((name, index) => {
      var prevColumnWidth = this.calculatedColumnWidths.get(index);
      if (prevColumnWidth != null) {
        calculatedColumnWidthsByName.set(name, prevColumnWidth);
      }
    });
    this.resetCalculatedColumnWidths();
    calculatedColumnWidthsByName.forEach((width, name) => {
      var index = model.getColumnIndexByName(name);
      if (index != null) {
        this.calculatedColumnWidths.set(index, width);
      }
    });
    trimMap(this.calculatedColumnWidths);
  }

  /**
   * Updates the user column widths based on the current model state
   * @param model The current IrisGridModel
   */
  updateUserColumnWidths(model) {
    this.userColumnWidths = new Map();
    this.userColumnWidthsByName.forEach((width, name) => {
      var modelIndex = model.getColumnIndexByName(name);
      if (modelIndex != null) {
        super.setColumnWidth(modelIndex, width);
      }
    });
  }

  /**
   * Updates the user and calculated column widths if the model columns have changed
   * @param model The current IrisGridModel
   */
  updateColumnWidthsIfNecessary(model) {
    // Comparing model.columns references wouldn't work here because
    // the reference can change in the model without the actual column definitions changing
    var modelColumnNames = this.getCachedCurrentModelColumnNames(model.columns);
    var isFirstCall = this.cachedModelColumnNames == null;
    if (this.cachedModelColumnNames != null && this.cachedModelColumnNames !== modelColumnNames && !deepEqual(modelColumnNames, this.cachedModelColumnNames)) {
      // Preserve column widths when possible to minimize visual shifts in the grid layout
      this.updateCalculatedColumnWidths(model);
      this.updateUserColumnWidths(model);
      this.cachedPaddingMaps.clear();
    } else if (isFirstCall && this.userColumnWidthsByName.size > 0) {
      // On the first call, project the seeded by-name widths into the
      // by-index map for the current model.
      this.updateUserColumnWidths(model);
    }
    this.cachedModelColumnNames = modelColumnNames;
    if (this.cachedHeaderGroupNames != null && !deepEqual(this.getCachedCurrentHeaderGroupNames(model.columnHeaderGroups), this.cachedHeaderGroupNames)) {
      this.resetCalculatedColumnWidths();
    }
    this.cachedHeaderGroupNames = model.columnHeaderGroups.map(group => group.name);
  }
  resetCalculatedHeaderGroupWidths() {
    this.cachedPaddingMaps.clear();
  }
  resetCalculatedColumnWidths() {
    super.resetCalculatedColumnWidths();
    this.resetCalculatedHeaderGroupWidths();
  }
  getGridY(state) {
    // The state here seems to be a GridMetricState with stateOverrides passed from IrisGrid in the props,
    // not guaranteed to be IrisGridMetricState
    var gridY = super.getGridY(state);
    var isFilterBarShown = state.isFilterBarShown,
      theme = state.theme,
      advancedFilters = state.advancedFilters,
      quickFilters = state.quickFilters,
      sorts = state.sorts,
      reverse = state.reverse;
    if (isFilterBarShown) {
      gridY += theme.filterBarHeight;
    } else if (quickFilters != null && quickFilters.size > 0 || advancedFilters != null && advancedFilters.size > 0) {
      gridY += theme.filterBarCollapsedHeight;
    }
    if (reverse && sorts != null && sorts.length > 0) {
      gridY += theme.reverseHeaderBarHeight;
    }
    return gridY;
  }

  /**
   * Gets the metrics for the current state. This method has to be called before setColumnSize or resetColumnSize.
   * @param state The current IrisGridMetricState
   * @returns The metrics for the current state
   */
  getMetrics(state) {
    var model = state.model;
    // Update column widths if columns in the cached model don't match the current model passed in the state
    this.updateColumnWidthsIfNecessary(model);
    return super.getMetrics(state);
  }

  /**
   * Sets the width for a specific column by index
   * @param column The index of the column to set
   * @param size The new width for the column
   */
  setColumnWidth(column, size) {
    super.setColumnWidth(column, size);
    assertNotNull(this.cachedModelColumnNames, 'setColumnWidth should be called after getMetrics');
    var name = this.cachedModelColumnNames[column];
    if (name != null) {
      this.userColumnWidthsByName.set(name, size);
      trimMap(this.userColumnWidthsByName);
    }
  }

  /**
   * Resets the width for a specific column by index
   * @param column The index of the column to reset
   */
  resetColumnWidth(column) {
    super.resetColumnWidth(column);
    assertNotNull(this.cachedModelColumnNames, 'resetColumnWidth should be called after getMetrics');
    var name = this.cachedModelColumnNames[column];
    if (name != null) {
      this.userColumnWidthsByName.delete(name);
    }
  }

  /**
   * Resets the user width for a column by its name. Works regardless of
   * whether the column is currently present in the model, so it is safe to
   * call when the model is about to be swapped (e.g. when applying a rollup).
   * @param name The name of the column to reset
   */
  resetColumnWidthByName(name) {
    this.userColumnWidthsByName.delete(name);
    if (this.cachedModelColumnNames != null) {
      var index = this.cachedModelColumnNames.indexOf(name);
      if (index >= 0) {
        super.resetColumnWidth(index);
      }
    }
  }

  /**
   * Resets all user column widths
   */
  resetAllColumnWidths() {
    this.userColumnWidths = new Map();
    this.userColumnWidthsByName = new Map();
  }

  /**
   * Gets the user column widths
   * @returns A map of user column widths
   */
  getUserColumnWidths() {
    // This might return stale data if getMetrics hasn't been called
    return this.userColumnWidths;
  }

  /**
   * Gets the user column widths keyed by column name. This is the source
   * of truth for hidden/manually-sized columns across model swaps and
   * persistence boundaries.
   * @returns A map of user column widths keyed by column name
   */
  getUserColumnWidthsByName() {
    return this.userColumnWidthsByName;
  }
  getCalculatedColumnWidths() {
    return this.calculatedColumnWidths;
  }

  /**
   * Calculate the width of the specified column's header
   * @param modelColumn ModelIndex of the column to get the header width for
   * @param state The grid metric state
   * @param maxColumnWidth Maximum allowed column width, applies only to leaf nodes
   * @returns The calculated width of the column header
   */
  calculateColumnHeaderWidth(modelColumn, state, maxColumnWidth) {
    var model = state.model;
    var parent = model.getColumnHeaderParentGroup(modelColumn, 0);
    var baseHeaderWidth = super.calculateColumnHeaderWidth(modelColumn, state, maxColumnWidth);

    // Column header with no grouping, use base implementation
    if (parent == null) {
      return baseHeaderWidth;
    }
    var headerPadding = this.getHeaderPadding(state, modelColumn, maxColumnWidth);
    return baseHeaderWidth + headerPadding;
  }

  // Original width of column header group content, including title, padding, icons, etc.
  // Does not include any rebalancing adjustments
  getColumnHeaderGroupWidth(modelColumn, depth, state, maxColumnWidth) {
    var model = state.model,
      theme = state.theme,
      context = state.context;
    var headerHorizontalPadding = theme.headerHorizontalPadding,
      headerFont = theme.headerFont;
    this.calculateLowerFontWidth(headerFont, context);
    this.calculateUpperFontWidth(headerFont, context);
    var padding = headerHorizontalPadding * 2;
    var headerText = model.textForColumnHeader(modelColumn, depth);
    var isColumnExpandable = isExpandableColumnGridModel(model) && model.isColumnExpandable(modelColumn);
    var expandCollapseIconWidth = isColumnExpandable ? theme.iconSize : 0;
    if (headerText !== undefined && headerText !== '') {
      return this.calculateTextWidth(context, headerFont, headerText, maxColumnWidth - padding) + padding + expandCollapseIconWidth;
    }
    return padding + expandCollapseIconWidth;
  }

  /**
   * Get metrics for positioning the filter bar input field.
   * @param index The visible index of the column to get the filter box coordinates for
   * @param state The current IrisGridMetricState
   * @param metrics The grid metrics
   * @returns Coordinates for the filter input field, or null if positioning cannot be calculated
   */
  // eslint-disable-next-line class-methods-use-this
  getFilterInputCoordinates(index, state, metrics) {
    // Only handle standard columns (>= 0) in the base implementation
    // Plugins can override to handle special columns (e.g., negative indices)
    if (index < 0) {
      return null;
    }
    var theme = state.theme;
    var _theme$filterBarHeigh = theme.filterBarHeight,
      filterBarHeight = _theme$filterBarHeigh === void 0 ? 0 : _theme$filterBarHeigh;
    var gridX = metrics.gridX,
      gridY = metrics.gridY,
      allColumnXs = metrics.allColumnXs,
      allColumnWidths = metrics.allColumnWidths;
    var columnX = allColumnXs.get(index);
    var columnWidth = allColumnWidths.get(index);
    if (columnX == null || columnWidth == null ||
    // Don't show the filter box for invisible columns
    columnWidth === 0 || filterBarHeight === 0) {
      return null;
    }
    return {
      x: gridX + columnX,
      y: gridY - filterBarHeight,
      width: columnWidth + 1,
      // cover right border
      height: filterBarHeight - 1 // remove bottom border
    };
  }

  /**
   * Calculate the new left index to bring the given column into view.
   * @param column The column that should be scrolled into view
   * @param state The current IrisGridMetricState
   * @param metrics The grid metrics
   * @returns The left column index to scroll to, or null if no scroll is needed
   */
  getScrollLeftForColumn(column, state, metrics) {
    var left = metrics.left,
      rightVisible = metrics.rightVisible,
      lastLeft = metrics.lastLeft;
    if (column < 0) {
      return null;
    }
    if (column < left) {
      // Column is to the left of visible area
      return column;
    }
    if (rightVisible < column) {
      // Column is to the right of visible area
      var newLeft = this.getLastLeft(state, column, this.getVisibleWidth(state));
      return Math.min(newLeft, lastLeft);
    }

    // Column is already visible, no scroll needed
    return null;
  }

  /**
   * Get the coordinates for the advanced filter button positioned in the filter bar.
   * @param index The column index
   * @param state The current IrisGridMetricState
   * @param metrics The grid metrics
   * @returns Coordinates for the advanced filter button, or null if it should not be shown
   */
  getAdvancedFilterButtonCoordinates(index, state, metrics) {
    var filterBoxCoordinates = this.getFilterInputCoordinates(index, state, metrics);
    if (filterBoxCoordinates == null) {
      return null;
    }
    var x = filterBoxCoordinates.x,
      y = filterBoxCoordinates.y,
      width = filterBoxCoordinates.width;
    return {
      x: x + width - 25,
      // Right edge of filter box (24px button + 1px for border)
      y: y + 2 // 2px top margin for the button
    };
  }
}
export default IrisGridMetricCalculator;
//# sourceMappingURL=IrisGridMetricCalculator.js.map